import { events, EventsChannel } from 'aws-amplify/api';
import { useEffect, useRef, useState } from 'react';
import {
  AmplifyChannelCreatorParams,
  AmplifyEventType,
  AmplifyEvent,
  EventPayloadWithExpiry,
  amplifyChannelEventExpiryTime,
  amplifyChannelNameCreators
} from './amplifyEventTypes';
import { useUser } from 'core/stores/hooks/useUser';

export const useAmplifyEventSubscribe = <T extends AmplifyEventType>({
  channelType,
  channelParams
}: {
  channelType: T;
  channelParams: AmplifyChannelCreatorParams[T];
}) => {
  const user = useUser();
  const [rawData, setRawData] = useState<EventPayloadWithExpiry<T>[]>([]);
  const cleanupIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const removeExpiredEvents = () => {
    const now = Date.now();
    setRawData((prevData) => prevData.filter((item) => item.expiryTime > now));
  };

  const clearIntervalRef = () => {
    const cleanupInterval = cleanupIntervalRef.current;
    if (cleanupInterval) {
      clearInterval(cleanupInterval);
      cleanupIntervalRef.current = null;
    }
  };

  useEffect(() => {
    if (rawData.length === 0) {
      clearIntervalRef();
    }
  }, [rawData]);

  useEffect(() => {
    let channel: EventsChannel | null = null;
    const connectAndSubscribe = async () => {
      try {
        const createChannelName = amplifyChannelNameCreators[channelType];
        channel = await events.connect(createChannelName(channelParams));

        channel.subscribe({
          next: (data) => {
            const event = data.event as AmplifyEvent<T>;
            // Filter out self events
            if (event.userId === user.id) return;
            const expiryNum = amplifyChannelEventExpiryTime[channelType];
            const item = {
              ...event,
              // NOTE: We add 1000 to prevent the events from flashing
              expiryTime: Date.now() + expiryNum + 1000
            };
            setRawData((prev) => [item, ...prev]);
            if (cleanupIntervalRef.current) return;
            const intervalNum = amplifyChannelEventExpiryTime[channelType];
            cleanupIntervalRef.current = setInterval(
              removeExpiredEvents,
              intervalNum
            );
          },
          error: (err) => console.error('error in subscription:', err)
        });
      } catch (err) {
        console.error('Connection handshake error:', err);
      }
    };
    connectAndSubscribe();

    return () => {
      channel?.close();
      clearIntervalRef();
    };
  }, []);
  return { data: rawData };
};
